import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { ReportManagementPageModel } from "../pageModels/ReportManagementPageModel";
import { Report } from "../../models/Report";
import { Radio, Table, Button, Modal, Input, message, Descriptions } from "antd";
import ImagePreview from "../../components/ImagePreview/ImagePreview";
import "../styles/ReportManagement.css";
import { globalStore } from "../../models/globalStore";

const model = new ReportManagementPageModel();

const ReportManagement: React.FC = observer(() => {
    const [processModalVisible, setProcessModalVisible] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedReport, setSelectedReport] = useState<Report | null>(null);
    const [processResult, setProcessResult] = useState("");
    const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
    const [previewImageSrc, setPreviewImageSrc] = useState("");
    const [previewImageTitle, setPreviewImageTitle] = useState("");

    useEffect(() => {
        globalStore.selectedMenuKey = "report-management";
        model.loadReports();
    }, []);

    const handleStatusChange = (status: number) => {
        model.setSelectedStatus(status);
        model.loadReports();
    };

    const handleProcessReport = (report: Report) => {
        setSelectedReport(report);
        setProcessResult("");
        setProcessModalVisible(true);
    };

    const handleImagePreview = (src: string, title: string) => {
        setPreviewImageSrc(src);
        setPreviewImageTitle(title);
        setImagePreviewVisible(true);
    };

    const handleSubmitProcess = async () => {
        if (!processResult.trim()) {
            message.error("请输入处理结果");
            return;
        }

        if (selectedReport) {
            if (selectedReport.status === 0) {
                const success = await model.markAsProcessed(selectedReport.id);
                if (success) {
                    message.success("标记成功");
                    setProcessModalVisible(false);
                    setDetailModalVisible(false);
                    setSelectedReport(null);
                    setProcessResult("");
                }
            } else {
                const success = await model.updateReportResult(selectedReport.id, processResult);
                if (success) {
                    message.success("更新成功");
                    setProcessModalVisible(false);
                    setDetailModalVisible(false);
                    setSelectedReport(null);
                    setProcessResult("");
                }
            }
        }
    };

    const handleViewDetail = (report: Report) => {
        setSelectedReport(report);
        setDetailModalVisible(true);
    };

    const getReportTypeText = (type: number) => {
        switch (type) {
            case 0: return "举报商品";
            case 1: return "举报卖家";
            case 2: return "举报买家";
            default: return "未知类型";
        }
    };

    const getReportTypeClass = (type: number) => {
        return `report-type-${type}`;
    };

    const columns = [
        {
            title: "举报类型",
            dataIndex: "type",
            key: "type",
            width: 70,
            render: (type: number) => (
                <span className={getReportTypeClass(type)}>
                    {getReportTypeText(type)}
                </span>
            )
        },
        {
            title: "举报目标",
            dataIndex: "reportTargetName",
            key: "reportTargetName",
            width: 150
        },
        {
            title: "举报内容",
            dataIndex: "reportReason",
            key: "reportReason",
            width: 200
        },
        {
            title: "处理结果",
            dataIndex: "processedDescript",
            key: "processedDescript",
            width: 200
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 80,
            render: (status: number) => status === 0 ? "待处理" : "已处理"
        },
        {
            title: "提交时间",
            dataIndex: "postTime",
            key: "postTime",
            width: 150,
            render: (time: Date) => time.toLocaleString()
        }
    ];

    return (
        <div className="report-management-container">
            <div className="filter-section">
                <Radio.Group 
                    value={model.selectedStatus} 
                    onChange={(e) => handleStatusChange(e.target.value)}
                >
                    <Radio.Button value={0}>全部</Radio.Button>
                    <Radio.Button value={1}>待处理</Radio.Button>
                    <Radio.Button value={2}>已处理</Radio.Button>
                </Radio.Group>
            </div>

            <div className="list-section">
                <Table
                    columns={columns}
                    dataSource={model.getDisplayReports()}
                    rowKey="id"
                    loading={model.loading}
                    pagination={false}
                    scroll={{ x: 1200 }}
                    onRow={(record) => ({
                        onClick: () => handleViewDetail(record)
                    })}
                />
            </div>

            {/* 处理弹框 */}
            <Modal
                title={selectedReport?.status === 0 ? "标记为已处理" : "编辑处理结果"}
                open={processModalVisible}
                onOk={handleSubmitProcess}
                onCancel={() => {
                    setProcessModalVisible(false);
                    setSelectedReport(null);
                    setProcessResult("");
                }}
            >
                <Input.TextArea
                    rows={4}
                    placeholder="请输入处理结果"
                    value={processResult}
                    onChange={(e) => setProcessResult(e.target.value)}
                />
            </Modal>

            {/* 详情弹框 */}
            <Modal
                title="举报详情"
                open={detailModalVisible}
                footer={null}
                onCancel={() => {
                    setDetailModalVisible(false);
                    setSelectedReport(null);
                }}
                width={700}
            >
                {selectedReport && (
                    <>
                        <Descriptions title="基本信息" column={2}>
                            <Descriptions.Item label="举报ID">{selectedReport.id}</Descriptions.Item>
                            <Descriptions.Item label="举报类型">
                                <span className={getReportTypeClass(selectedReport.type)}>
                                    {getReportTypeText(selectedReport.type)}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label="举报目标">{selectedReport.reportTargetName}</Descriptions.Item>
                            <Descriptions.Item label="目标ID">{selectedReport.reportTargetId}</Descriptions.Item>
                            <Descriptions.Item label="提交时间">{selectedReport.postTime.toLocaleString()}</Descriptions.Item>
                            <Descriptions.Item label="状态">{selectedReport.status === 0 ? "待处理" : "已处理"}</Descriptions.Item>
                        </Descriptions>

                        <Descriptions title="举报内容" column={1}>
                            <Descriptions.Item label="举报原因">{selectedReport.reportReason}</Descriptions.Item>
                            <Descriptions.Item label="截图证据">
                                <div className="image-evidence">
                                    {selectedReport.imageEvidences.map((img, index) => (
                                        <img 
                                            key={index} 
                                            src={img} 
                                            alt={`证据${index + 1}`}
                                            onClick={() => handleImagePreview(img, `证据${index + 1}`)}
                                        />
                                    ))}
                                </div>
                            </Descriptions.Item>
                        </Descriptions>

                        {selectedReport.status === 1 && (
                            <Descriptions title="处理信息" column={1}>
                                <Descriptions.Item label="处理结果">{selectedReport.processedDescript}</Descriptions.Item>
                            </Descriptions>
                        )}

                        <div style={{ marginTop: 24, textAlign: 'right' }}>
                            <Button
                                type={selectedReport.status === 0 ? "primary" : "default"}
                                onClick={() => handleProcessReport(selectedReport)}
                            >
                                {selectedReport.status === 0 ? "标记为已处理" : "编辑处理结果"}
                            </Button>
                        </div>
                    </>
                )}
            </Modal>

            {/* 图片预览 */}
            <ImagePreview
                visible={imagePreviewVisible}
                src={previewImageSrc}
                title={previewImageTitle}
                onClose={() => {
                    setImagePreviewVisible(false);
                    setPreviewImageSrc("");
                    setPreviewImageTitle("");
                }}
            />
        </div>
    );
});

export default ReportManagement; 